<script lang="ts" setup>
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import { ref, onBeforeMount, computed, watch, onServerPrefetch } from 'vue';
import { fetch } from '@@home/plugins/fetch';
import { useCooperationStore } from '@@home/stores/prefetch';
import { groupBy } from 'lodash-es';
import {
  CONTRIBUTOR_CATEGORY_SUB_PLATINUM,
  CONTRIBUTOR_CATEGORY_SUB_GOLD,
  CONTRIBUTOR_CATEGORY_SUB_SILVER,
  CONTRIBUTOR_CATEGORY_SUB_SPONSOR,
} from '@@home/utils/constants';

const store = useCooperationStore();

// 每页数量
const limit = 15;

const searchEl = ref(null);
const isOpen = ref(false);
const category = ref<any>([]);
// 主分类ID
const mainValue = ref('');
// 子分类ID
const subValue = ref('');
const search = ref('');
const listing = ref<any>([]);
const total = ref(0);
// 数据的当前页数
const currentPage = ref(1);
// 用户输入的当前页数
const current = ref(1);

const totalPage = computed(() => {
  return Math.ceil(total.value / limit);
});

// 浏览器获取捐赠人详细列表
const getList = async () => {
  const { isSuccess, data } = await fetch('/donor/v1/list', {
    data: {
      mc: mainValue.value,
      sc: subValue.value,
      donorName: search.value,
      offset: (currentPage.value - 1) * limit,
      limit,
    }
  });

  if (isSuccess) {
    listing.value = data.donorList;
    total.value = data.totalCount;
  }

  return isSuccess;
}

// 服务器或浏览器获取捐赠人 LOGO
const getContributors = async () => {
  const { isSuccess, data } = await fetch('/donor/v1/show', {
    data: {
      limit: -1,
    }
  });

  if (isSuccess) {
    store.contributors = groupBy(data, 'donateLevel');
  }

  return isSuccess;
}

const debouncedGetList = useDebounceFn(() => {
  getList();
}, 100);

const subCategory = computed(() => {
  if (!mainValue.value) {
    return [];
  }

  return category.value.find((item: any) => item.categoryId === mainValue.value)?.subCategory ?? [];
});

const previous = () => {
  if (currentPage.value < 2) {
    return;
  }

  currentPage.value--;
  current.value = currentPage.value;

  debouncedGetList();
}

const next = () => {
  if (currentPage.value >= totalPage.value) {
    return;
  }

  currentPage.value++;
  current.value = currentPage.value;

  debouncedGetList();
}

// 提交页码
const submit = () => {
  if (isNaN(Number(current.value))) {
    current.value = 1;
  }

  if (current.value < 1 || current.value > totalPage.value) {
    return;
  }

  currentPage.value = current.value;
  debouncedGetList();
}

// 提交搜索
const submitSearch = () => {
  currentPage.value = 1;
  current.value = 1;
  debouncedGetList();
}

const blur = () => {
  current.value = currentPage.value;
}

onClickOutside(searchEl, () => {
  isOpen.value = false;
});

onServerPrefetch(async () => {
  await getContributors();
});

onBeforeMount(async () => {
  if (!store.contributors) {
    await getContributors();
  }

  const { isSuccess, data } = await fetch('/donor/v1/category/selector');

  if (isSuccess) {
    category.value = data;
  }

  await getList();
});

watch(mainValue, () => {
  subValue.value = '';
  currentPage.value = 1;
  current.value = 1;

  debouncedGetList();
});

watch(subValue, () => {
  currentPage.value = 1;
  current.value = 1;

  debouncedGetList();
});
</script>

<template>
  <section class="page-cooperation-partner">
    <div class="cooperation-partner-container mx-auto">
      <h3 class="m-0 text-center font-medium">{{ $t('cooperation.partner.title') }}</h3>

      <div class="cooperation-partner-content bg-contain bg-top bg-no-repeat">
        <div class="partner-content-box mx-auto">
          <div class="partner-content-bd__toolbar flex items-end justify-between">
            <div class="filters">
              <ul class="category-main m-0 flex list-none p-0">
                <li>
                  <label>
                    <input type="radio" name="main" value="" v-model="mainValue">
                    <span class="label-text">{{ $t('cooperation.partner.searchCategoryAll') }}</span>
                  </label>
                </li>
                <li v-for="item in category" :key="item.categoryId">
                  <label>
                    <input type="radio" name="main" :value="item.categoryId" v-model="mainValue">
                    <span class="label-text">{{ item.categoryName }}</span>
                  </label>
                </li>
              </ul>
              <ul class="category-sub m-0 flex list-none p-0">
                <li v-if="mainValue">
                  <label>
                    <input type="radio" name="sub" value="" v-model="subValue">
                    <span class="label-text" value="">{{ $t('cooperation.partner.searchCategoryAll') }}</span>
                  </label>
                </li>
                <li v-for="item in subCategory" :key="item.categoryId">
                  <label :title="item.categoryName">
                    <input type="radio" name="sub" :value="item.categoryId" v-model="subValue">
                    <span class="label-text"><span class="block max-w-90px truncate">{{ item.categoryName }}</span></span>
                  </label>
                </li>
              </ul>
            </div>
            <div class="search relative" :class="{ active: isOpen }" ref="searchEl">
              <div class="input-group relative flex">
                <div class="icon flex items-center justify-center">
                  <span class="i-home-icons:search"></span>
                </div>
                <input type="text" class="block flex-grow border-0 focus:outline-none" :placeholder="$t('cooperation.partner.searchInputPlaceholder')" @focus="isOpen = true" v-model="search" @keydown.enter="submitSearch">
              </div>
              <!-- <div class="popover absolute left-0 right-0" v-show="isOpen">
                <dl class="m-0">
                  <dt class="m-0">{{ $t('cooperation.partner.searchInputTitle1') }}</dt>
                  <dd class="m-0">
                    <ul class="m-0 flex list-none p-0">
                      <li>白金捐赠人</li>
                      <li>白金捐赠人</li>
                    </ul>
                  </dd>
                  <dt class="m-0">{{ $t('cooperation.partner.searchInputTitle2') }}</dt>
                  <dd class="m-0">
                    <ul class="m-0 flex list-none p-0">
                      <li>白金捐赠人</li>
                      <li>白金捐赠人</li>
                    </ul>
                  </dd>
                </dl>
              </div> -->
            </div>
          </div>
          <div class="partner-content-bd__table min-h-825px">
            <table class="rounded-corners w-full">
              <thead>
                <tr>
                  <th>{{ $t('cooperation.partner.tableHead2') }}</th>
                  <th width="25%">{{ $t('cooperation.partner.tableHead3') }}</th>
                  <th width="18%">{{ $t('cooperation.partner.tableHead4') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="listing.length">
                  <tr v-for="item in listing" :key="item.donorId">
                    <td>{{ item.donorName }}</td>
                    <td>{{ item.donateTarget }}</td>
                    <td>{{ item.donateLevel }}</td>
                  </tr>
                </template>
                <template v-else>
                  <tr>
                    <td colspan="3">{{ $t('cooperation.partner.tableEmpty') }}</td>
                  </tr>
                </template>
              </tbody>
            </table>

            <p class="m-0 mt-8px text-14px color-hex-9A9EA6">{{ $t('cooperation.partner.contributorNotice') }}</p>

            <div class="pagination flex justify-center" v-if="listing.length">
              <div class="pagination__container flex items-center">
                <div class="previous">
                  <a herf="javascript:;" class="block cursor-pointer" :class="{ invisible: currentPage < 2 }" @click="previous">
                    <span class="i-home-icons:arrow-left block"></span>
                  </a>
                </div>
                <div class="input box-border">
                  <input type="text" class="m-0 block h-full w-full border-0 bg-transparent p-0 text-center outline-none" v-model="current" @keydown.enter="submit" @blur="blur">
                </div>
                <div class="separator">/</div>
                <div class="total">{{ totalPage }}</div>
                <div class="next">
                  <a herf="javascript:;" class="block cursor-pointer" :class="{ invisible: currentPage >= totalPage }" @click="next">
                    <span class="i-home-icons:arrow-right block"></span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template v-if="store.contributors">
        <div class="contributors">
          <h4 class="relative m-0 flex items-center justify-between text-center font-medium after:block after:w-full after:content-empty before:block before:w-full before:content-empty">
            <span class="relative z-1 inline-block flex-shrink-0">{{ $t('cooperation.partner.contributor1') }}</span>
          </h4>

          <ul class="platinum-contributors m-0 flex flex-shrink flex-wrap list-none p-0">
            <li v-for="item in store.contributors[CONTRIBUTOR_CATEGORY_SUB_PLATINUM]" :key="item.donorId">
              <img :data-src="item.logo" class="lazyload" :alt="item.donorName" :title="item.donorName">
            </li>
          </ul>
        </div>

        <div class="contributors">
          <h4 class="relative m-0 flex items-center justify-between text-center font-medium after:block after:w-full after:content-empty before:block before:w-full before:content-empty">
            <span class="relative z-1 inline-block flex-shrink-0">{{ $t('cooperation.partner.contributor2') }}</span>
          </h4>

          <ul class="platinum-contributors m-0 flex flex-shrink flex-wrap list-none p-0">
            <li v-for="item in store.contributors[CONTRIBUTOR_CATEGORY_SUB_GOLD]" :key="item.donorId">
              <img :data-src="item.logo" class="lazyload" :alt="item.donorName" :title="item.donorName">
            </li>
          </ul>
        </div>

        <div class="contributors">
          <h4 class="relative m-0 flex items-center justify-between text-center font-medium after:block after:w-full after:content-empty before:block before:w-full before:content-empty">
            <span class="relative z-1 inline-block flex-shrink-0">{{ $t('cooperation.partner.contributor3') }}</span>
          </h4>

          <ul class="platinum-contributors m-0 flex flex-shrink flex-wrap list-none p-0">
            <li v-for="item in store.contributors[CONTRIBUTOR_CATEGORY_SUB_SILVER]" :key="item.donorId">
              <img :data-src="item.logo" class="lazyload" :alt="item.donorName" :title="item.donorName">
            </li>
          </ul>
        </div>

        <div class="contributors">
          <h4 class="relative m-0 flex items-center justify-between text-center font-medium after:block after:w-full after:content-empty before:block before:w-full before:content-empty">
            <span class="relative z-1 inline-block flex-shrink-0">{{ $t('cooperation.partner.contributor4') }}</span>
          </h4>

          <ul class="platinum-contributors m-0 flex flex-shrink flex-wrap list-none p-0">
            <li v-for="item in store.contributors[CONTRIBUTOR_CATEGORY_SUB_SPONSOR]" :key="item.donorId">
              <img :data-src="item.logo" class="lazyload" :alt="item.donorName" :title="item.donorName">
            </li>
          </ul>
        </div>
      </template>

    </div>
  </section>
</template>

<style lang="scss" scoped>
.page-cooperation-partner {
  .cooperation-partner-container {
    width: 1360px;

    h3 {
      font-size: 32px;
    }

    .cooperation-partner-content {
      min-height: 560px;
      margin-top: 32px;
      background-image: url(@@home/assets/cooperation/partner/bg.svg);

      .partner-content-box {
        width: 1200px;
        padding-top: 48px;

        .partner-content-bd__toolbar {
          .filters {
            color: #fff;

            label {
              cursor: pointer;
            }

            li + li {
              margin-left: 32px;
            }

            .category-main {
              /**remove default style**/
              [type="radio"]:not(:checked),
              [type="radio"]:checked {
                position: absolute;
                left: -9999px;
                visibility: hidden;
              }

              [type="radio"] + .label-text {
                position: relative;
                display: block;
                transition: .28s ease;
              }

              [type="radio"] + .label-text:after {
                position: absolute;
                bottom: -8px;
                left: 50%;
                z-index: 0;
                width: 32px;
                height: 2px;
                margin-left: -16px;
                background: #fff;
                border-radius: 1px;
                transition: .28s ease;
                content: '';
              }

              /* Unchecked styles */
              [type="radio"]:not(:checked) + .label-text:after {
                opacity: 0;
              }

              /* Checked styles */
              [type="radio"]:checked + .label-text:after {
                opacity: 1;
              }
            }

            .category-sub {
              min-height: 16px;
              margin-top: 24px;

              /**remove default style**/
              [type="radio"]:not(:checked),
              [type="radio"]:checked {
                position: absolute;
                left: -9999px;
                visibility: hidden;
              }

              [type="radio"] + .label-text {
                position: relative;
                display: block;
                margin-left: 16px;
                font-size: 14px;
                transition: .28s ease;
              }

              [type="radio"] + .label-text:after {
                position: absolute;
                top: -5px;
                right: -16px;
                bottom: -5px;
                left: -16px;
                z-index: 0;
                background: rgba(255, 255, 255, 0.2);
                border-radius: 2px;
                transition: .28s ease;
                content: '';
              }

              /* Unchecked styles */
              [type="radio"]:not(:checked) + .label-text:after {
                opacity: 0;
              }

              /* Checked styles */
              [type="radio"]:checked + .label-text:after {
                opacity: 1;
              }
            }
          }

          .search {
            &.active {
              .input-group {
                background-color: #fbfbfb;
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);

                input {
                  color: #2d2d2d;

                  &::placeholder {
                    color: #9a9ea6;
                  }
                }

                .icon {
                  span {
                    color: #1180ff;
                  }
                }
              }
            }

            &:hover:not(.active) {
              .input-group {
                background-color: rgba(255, 255, 255, 0.5);
              }
            }

            .input-group {
              z-index: 1;
              width: 320px;
              height: 48px;
              background-color: rgba(255, 255, 255, 0.2);
              border-radius: 4px;

              .icon {
                width: 48px;

                span {
                  width: 16px;
                  height: 16px;
                  color: #fff;
                }
              }

              input {
                color: #fff;
                font-size: 16px;
                background-color: transparent;

                &::placeholder {
                  color: #fff;
                }
              }
            }

            .popover {
              top: 100%;
              z-index: 0;
              margin-top: -4px;
              background: #fff;
              border-radius: 0 0 4px 4px;

              dl {
                padding-top: 12px;
                padding-right: 16px;
                padding-bottom: 24px;
                padding-left: 16px;

                dt {
                  margin-top: 8px;
                  color: #9a9ea6;
                  font-size: 12px;
                }

                dd {
                  margin-top: 8px;

                  li {
                    padding: 8px;
                    color: #2d2d2d;
                    font-size: 12px;
                    background-color: #fbfbfb;
                    border-radius: 2px;
                    @apply cursor-pointer;

                    &:hover {
                      color: #1180ff;
                      background-color: rgba(17, 128, 255, 0.05);
                    }

                    + li {
                      margin-left: 24px;
                    }
                  }
                }
              }
            }
          }
        }

        .partner-content-bd__table {
          margin-top: 32px;
          padding: 48px 80px;
          background-color: #fff;
          border-radius: 4px;
          box-shadow: 0 0 60px 0 rgba(17, 128, 255, 0.1);

          .rounded-corners {
            --border: 1px solid #1180ff;
            overflow: hidden;
            border: var(--border);
            border-radius: 4px;
            border-collapse: separate;
            border-spacing: 0;

            th:not(:last-child),
            td:not(:last-child) {
              border-right: var(--border);
            }

            > thead > tr:not(:last-child) > th,
            > thead > tr:not(:last-child) > td,
            > tbody > tr:not(:last-child) > th,
            > tbody > tr:not(:last-child) > td,
            > tfoot > tr:not(:last-child) > th,
            > tfoot > tr:not(:last-child) > td,
            > tr:not(:last-child) > td,
            > tr:not(:last-child) > th,
            > thead:not(:last-child),
            > tbody:not(:last-child),
            > tfoot:not(:last-child) {
              border-bottom: var(--border);
            }
          }

          table {
            th {
              height: 56px;
              color: #fff;
              font-size: 24px;
              background-color: #1180ff;

              @apply font-medium;
            }

            td {
              height: 48px;
              font-size: 16px;

              @apply text-center;
            }
          }

          .pagination {
            margin-top: 24px;
            font-size: 14px;

            .previous {
              &:hover {
                color: #1180ff;
              }
            }

            .input {
              width: 32px;
              height: 24px;
              margin-left: 10px;
              padding-right: 6px;
              padding-left: 6px;
              border: 1px solid #9a9ea6;
              border-radius: 4px;

              &:hover,
              &:focus-within {
                border: 1px solid #1180ff;
              }
            }

            .separator {
              margin-left: 13px;
            }

            .total {
              margin-left: 10px;
            }

            .next {
              margin-left: 10px;

              &:hover {
                color: #1180ff;
              }
            }
          }
        }
      }
    }

    .contributors {
      h4 {
        margin-top: 48px;
        font-size: 24px;

        span {
          padding: 0 32px;
        }

        &::before {
          height: 2px;
          background: linear-gradient(116.01deg, rgba(91, 230, 255, 0) 0%, rgba(17, 128, 255, 0.5) 100%);
          opacity: 0.2;
        }

        &::after {
          height: 2px;
          background: linear-gradient(243.99deg, rgba(91, 230, 255, 0) 0%, rgba(17, 128, 255, 0.5) 100%);
          opacity: 0.2;
        }
      }

      ul {
        margin-top: 32px;

        li {
          width: 213px;
          height: 78px;
          margin-left: 16px;
          overflow: hidden;
          background-color: #fff;
          border-radius: 4px;

          @apply overflow-hidden;

          &:nth-child(n+7) {
            margin-top: 16px;
          }

          &:nth-child(6n + 1) {
            margin-left: 0;
          }

          &:hover {
            img {
              width: 110%;
              height: 110%;
              margin-top: -2%;
              margin-left: -5%;
            }
          }

          img {
            display: block;
            width: 100%;
            height: 100%;
            transition: all 0.3s;

            @apply object-contain;
          }
        }
      }
    }
  }
}
</style>
